import {
  EMPLOYMENT_TYPE_OPTIONS,
  EmploymentType,
  optionLabel,
  publicJobUrl,
  SectorType,
  WorkType,
} from './JobModel'

/** Paylaşım metni için gereken ilan alanları (IJobModel ve IPublicJobModel ikisi de uyar) */
export interface IShareableJob {
  id: string
  title: string
  subTitle?: string | null
  description: string
  sector?: SectorType | null
  workType: WorkType
  employmentType: EmploymentType
  locationList: {province: string; district: string}[]
}

const MAX_QUALIFICATIONS = 10
const MAX_HASHTAGS = 10

const WORK_MODEL_TEXT: Record<WorkType, string> = {
  REMOTE: '%100 Remote',
  HYBRID: 'Hibrit',
  ONSITE: 'İş yerinde',
}

const SECTOR_HASHTAGS: Record<SectorType, string[]> = {
  IT: ['ITJobs', 'Teknoloji'],
  FINANCE: ['Finans', 'FinanceJobs'],
  HEALTHCARE: ['Sağlık', 'HealthcareJobs'],
  EDUCATION: ['Eğitim', 'EducationJobs'],
}

const WORK_TYPE_HASHTAGS: Record<WorkType, string[]> = {
  REMOTE: ['RemoteWork'],
  HYBRID: ['HybridWork'],
  ONSITE: [],
}

/**
 * Editörde yazılmış açıklamadan "Aradığımız Nitelikler" maddeleri: önce listeler (madde
 * işaretli / numaralı), liste yoksa paragraflar.
 */
function extractQualifications(html: string): string[] {
  const document = new DOMParser().parseFromString(html || '', 'text/html')
  const clean = (text: string | null) => (text || '').replace(/\s+/g, ' ').trim()
  const listItems = Array.from(document.querySelectorAll('li')).map((item) => clean(item.textContent))
  const items = listItems.length > 0 ? listItems : Array.from(document.querySelectorAll('p')).map((p) => clean(p.textContent))
  return items.filter((item) => item.length > 0).slice(0, MAX_QUALIFICATIONS)
}

/** Başlıktaki kelimelerden etiket: "MySQL DBA" → #MySQL #DBA */
function titleHashtags(title: string): string[] {
  return title
    .split(/[\s/,&()+\-–]+/)
    .map((word) => word.replace(/[^0-9A-Za-zÇĞİÖŞÜçğıöşü#]/g, '').replace(/^#/, ''))
    .filter((word) => word.length >= 2)
}

function hashtags(job: IShareableJob): string {
  const tags = [
    ...titleHashtags(job.title),
    ...(job.sector ? SECTOR_HASHTAGS[job.sector] : []),
    ...WORK_TYPE_HASHTAGS[job.workType],
    'Hiring',
    'Recruitment',
    'İşİlanı',
  ]
  const seen = new Set<string>()
  return tags
    .filter((tag) => {
      const key = tag.toLocaleLowerCase('tr-TR')
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .slice(0, MAX_HASHTAGS)
    .map((tag) => `#${tag}`)
    .join(' ')
}

/**
 * Sosyal medya paylaşım metni (LinkedIn örneğindeki biçim):
 * başlık, kısa tanıtım, nitelik maddeleri, çalışma modeli, lokasyon, başvuru linki ve etiketler.
 */
export function buildJobPost(job: IShareableJob): string {
  const lines: string[] = [`🔎 ${job.title} Arıyoruz!`]
  lines.push(job.subTitle?.trim() || `Ekibimize katılacak ${job.title} arıyoruz.`)

  const qualifications = extractQualifications(job.description)
  if (qualifications.length > 0) {
    lines.push('📌 Aradığımız Nitelikler:')
    qualifications.forEach((item) => lines.push(`• ${item}`))
  }

  lines.push(
    `🏠 Çalışma Modeli: ${WORK_MODEL_TEXT[job.workType]} · ${optionLabel(EMPLOYMENT_TYPE_OPTIONS, job.employmentType)}`
  )
  if (job.locationList.length > 0) {
    lines.push(
      `📍 Lokasyon: ${job.locationList
        .map((location) => (location.district ? `${location.province} / ${location.district}` : location.province))
        .join(', ')}`
    )
  }
  lines.push(`📩 Başvuru için: ${publicJobUrl(job.id)}`)
  lines.push(hashtags(job))
  return lines.join('\n')
}
